import { useCallback, useEffect, useRef, useState } from 'react';
import {
  WS_PATH,
  isActiveState,
  type AudioLevels,
  type LogLine,
  type ServerEvent,
  type SessionSummary,
} from '@teams-avatar/shared';

export type ConnectionState = 'connecting' | 'open' | 'closed';

interface Stream {
  connection: ConnectionState;
  sessions: SessionSummary[];
  serverVersion: string | null;
  maxConcurrentSessions: number | null;
  lastEventAt: string | null;
  /** Live-Log je Session, nur fuer abonnierte. */
  logs: Record<string, LogLine[]>;
  /** Letzter Audiopegel je Session, nur fuer abonnierte. */
  audio: Record<string, AudioLevels>;
  subscribe: (sessionId: string) => void;
  unsubscribe: (sessionId: string) => void;
}

/** Damit ein langlaufendes Meeting den Browser nicht vollschreibt. */
const MAX_LOG_LINES_PER_SESSION = 500;

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15_000;

/**
 * Lebenszeichen an den Server. Beantwortet er mit 'pong'.
 *
 * Ohne das faellt eine still gestorbene Verbindung gar nicht auf: wechselt das
 * Handy von WLAN auf Mobilfunk oder raeumt ein Router die Verbindung ab, bleibt
 * der Socket in readyState OPEN stehen und es kommt einfach nichts mehr. Die
 * Oberflaeche zeigt dann "Live" und veraltete Daten — das ist schlimmer als
 * "Getrennt", weil man es nicht sieht.
 */
const HEARTBEAT_MS = 25_000;
/** Kommt so lange gar nichts (auch kein pong), gilt die Verbindung als tot. */
const SILENCE_TIMEOUT_MS = 70_000;

function wsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${WS_PATH}`;
}

/**
 * Haelt die Verbindung zum Status-Stream offen und baut sie mit steigendem
 * Backoff wieder auf. Der Server schickt beim Connect einen vollen Snapshot,
 * deshalb braucht der Reconnect keine Nachladelogik.
 */
export function useSessionStream(): Stream {
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [maxConcurrentSessions, setMaxConcurrent] = useState<number | null>(null);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [logs, setLogs] = useState<Record<string, LogLine[]>>({});
  const [audio, setAudio] = useState<Record<string, AudioLevels>>({});
  const subscriptionsRef = useRef<Set<string>>(new Set());

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);
  const disposedRef = useRef(false);

  const handleEvent = useCallback((event: ServerEvent) => {
    setLastEventAt(new Date().toISOString());
    switch (event.type) {
      case 'hello':
        setServerVersion(event.version);
        setMaxConcurrent(event.maxConcurrentSessions);
        return;
      case 'sessions':
        setSessions(event.sessions);
        return;
      case 'session.updated':
        setSessions((current) => {
          const index = current.findIndex((session) => session.id === event.session.id);
          if (index === -1) return [event.session, ...current];
          const next = current.slice();
          next[index] = event.session;
          return next;
        });
        return;
      case 'session.removed':
        setSessions((current) => current.filter((session) => session.id !== event.sessionId));
        return;
      case 'session.log':
        setLogs((current) => {
          const existing = current[event.sessionId] ?? [];
          const next = [...existing, event.line];
          return {
            ...current,
            [event.sessionId]: next.length > MAX_LOG_LINES_PER_SESSION
              ? next.slice(-MAX_LOG_LINES_PER_SESSION)
              : next,
          };
        });
        return;
      case 'session.audio':
        // Nur der letzte Wert: eine Historie im Sekundentakt braucht niemand,
        // und sie waere in einer Stunde Meeting 3600 Eintraege lang.
        setAudio((current) => ({ ...current, [event.sessionId]: event.audio }));
        return;
      case 'pong':
        return;
    }
  }, []);

  useEffect(() => {
    disposedRef.current = false;

    const connect = (): void => {
      if (disposedRef.current) return;
      setConnection('connecting');

      const socket = new WebSocket(wsUrl());
      socketRef.current = socket;
      let lastMessageAt = Date.now();
      let heartbeat: number | undefined;

      const stopHeartbeat = (): void => {
        if (heartbeat !== undefined) window.clearInterval(heartbeat);
        heartbeat = undefined;
      };

      socket.onopen = () => {
        attemptRef.current = 0;
        setConnection('open');
        // Nach einem Reconnect die Abos erneuern — der Server kennt sie nicht mehr.
        for (const sessionId of subscriptionsRef.current) {
          socket.send(JSON.stringify({ type: 'subscribe', sessionId }));
        }

        lastMessageAt = Date.now();
        heartbeat = window.setInterval(() => {
          if (Date.now() - lastMessageAt > SILENCE_TIMEOUT_MS) {
            // Selbst schliessen statt warten: onclose loest den Reconnect aus.
            // Sonst stuende die Oberflaeche beliebig lange auf "Live" und zeigte
            // veraltete Sessions.
            stopHeartbeat();
            socket.close();
            return;
          }
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }));
          }
        }, HEARTBEAT_MS);
      };

      socket.onmessage = (message) => {
        lastMessageAt = Date.now();
        try {
          handleEvent(JSON.parse(String(message.data)) as ServerEvent);
        } catch {
          // Unlesbare Nachricht ignorieren, statt die Ansicht zu zerlegen.
        }
      };

      socket.onclose = () => {
        stopHeartbeat();
        socketRef.current = null;
        setConnection('closed');
        if (disposedRef.current) return;
        const attempt = attemptRef.current++;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
        timerRef.current = window.setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // onclose folgt direkt danach und uebernimmt den Reconnect.
      };
    };

    connect();

    return () => {
      disposedRef.current = true;
      if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [handleEvent]);

  const subscribe = useCallback((sessionId: string) => {
    subscriptionsRef.current.add(sessionId);
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'subscribe', sessionId }));
    }
  }, []);

  const unsubscribe = useCallback((sessionId: string) => {
    subscriptionsRef.current.delete(sessionId);
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'unsubscribe', sessionId }));
    }
  }, []);

  return {
    connection,
    sessions,
    serverVersion,
    maxConcurrentSessions,
    lastEventAt,
    logs,
    audio,
    subscribe,
    unsubscribe,
  };
}

export function splitSessions(sessions: SessionSummary[]): {
  active: SessionSummary[];
  history: SessionSummary[];
} {
  const active: SessionSummary[] = [];
  const history: SessionSummary[] = [];
  for (const session of sessions) {
    if (isActiveState(session.state)) active.push(session);
    else history.push(session);
  }
  return { active, history };
}
